import { computed, del, ref, set } from "vue";
import { useWorkflowStepStore } from "@/stores/workflowStepStore";
import { pushOrSet } from "@/utils/pushOrSet";
import { defineScopedStore } from "./scopedStore";
function updateTerminalToTerminal(connections) {
    const inputTerminalToOutputTerminals = {};
    connections.forEach((connection) => {
        const terminals = getTerminals(connection);
        const inputTerminalId = getTerminalId(terminals.input);
        pushOrSet(inputTerminalToOutputTerminals, inputTerminalId, terminals.output);
    });
    return inputTerminalToOutputTerminals;
}
function updateTerminalToConnection(connections) {
    const terminalToConnection = {};
    connections.forEach((connection) => {
        const terminals = getTerminals(connection);
        const outputTerminalId = getTerminalId(terminals.output);
        pushOrSet(terminalToConnection, outputTerminalId, connection);
        const inputTerminalId = getTerminalId(terminals.input);
        pushOrSet(terminalToConnection, inputTerminalId, connection);
    });
    return terminalToConnection;
}
function updateStepToConnections(connections) {
    const stepToConnections = {};
    connections.forEach((connection) => {
        pushOrSet(stepToConnections, connection.input.stepId, connection);
        pushOrSet(stepToConnections, connection.output.stepId, connection);
    });
    return stepToConnections;
}
export function getTerminalId(item) {
    return `node-${item.stepId}-${item.connectorType}-${item.name}`;
}
export function getTerminals(item) {
    return {
        input: { stepId: item.input.stepId, name: item.input.name, connectorType: "input" },
        output: { stepId: item.output.stepId, name: item.output.name, connectorType: "output" },
    };
}
export function getConnectionId(item) {
    return `${item.input.stepId}-${item.input.name}-${item.output.stepId}-${item.output.name}`;
}
export const useConnectionStore = defineScopedStore("workflowConnectionStore", (workflowId) => {
    const connections = ref([]);
    const invalidConnections = ref({});
    const inputTerminalToOutputTerminals = ref({});
    const terminalToConnection = ref({});
    const stepToConnections = ref({});
    function $reset() {
        connections.value = [];
        invalidConnections.value = {};
        inputTerminalToOutputTerminals.value = {};
        terminalToConnection.value = {};
        stepToConnections.value = {};
    }
    const getOutputTerminalsForInputTerminal = computed(() => (terminalId) => inputTerminalToOutputTerminals.value[terminalId] || []);
    const getConnectionsForTerminal = computed(() => (terminalId) => terminalToConnection.value[terminalId] || []);
    const getConnectionsForStep = computed(() => (stepId) => stepToConnections.value[stepId] || []);
    const stepStore = useWorkflowStepStore(workflowId);
    function addConnection(connection) {
        Object.freeze(connection);
        connections.value.push(connection);
        stepStore.addConnection(connection);
        terminalToConnection.value = updateTerminalToConnection(connections.value);
        inputTerminalToOutputTerminals.value = updateTerminalToTerminal(connections.value);
        stepToConnections.value = updateStepToConnections(connections.value);
    }
    function removeConnection(terminal) {
        connections.value = connections.value.filter((connection) => {
            const id = getConnectionId(connection);
            if (typeof terminal === "string") {
                if (id === terminal) {
                    stepStore.removeConnection(connection);
                    del(invalidConnections.value, id);
                    return false;
                }
                else {
                    return true;
                }
            }
            else if (terminal.connectorType === "input") {
                if (connection.input.stepId == terminal.stepId && connection.input.name == terminal.name) {
                    stepStore.removeConnection(connection);
                    del(invalidConnections.value, id);
                    return false;
                }
                else {
                    return true;
                }
            }
            else {
                if (connection.output.stepId == terminal.stepId && connection.output.name == terminal.name) {
                    stepStore.removeConnection(connection);
                    del(invalidConnections.value, id);
                    return false;
                }
                else {
                    return true;
                }
            }
        });
        terminalToConnection.value = updateTerminalToConnection(connections.value);
        inputTerminalToOutputTerminals.value = updateTerminalToTerminal(connections.value);
        stepToConnections.value = updateStepToConnections(connections.value);
    }
    function markInvalidConnection(connectionId, reason) {
        set(invalidConnections.value, connectionId, reason);
    }
    function dropFromInvalidConnections(connectionId) {
        del(invalidConnections.value, connectionId);
    }
    return {
        connections,
        invalidConnections,
        inputTerminalToOutputTerminals,
        terminalToConnection,
        stepToConnections,
        $reset,
        getOutputTerminalsForInputTerminal,
        getConnectionsForTerminal,
        getConnectionsForStep,
        addConnection,
        removeConnection,
        markInvalidConnection,
        dropFromInvalidConnections,
    };
});
